import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loading, EmptyState, StatusBadge } from "@/components/common";
import { formatDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Users as UsersIcon, Pencil, KeyRound, ShieldCheck, RotateCcw } from "lucide-react";

export default function Users() {
  const { can } = useAuth();
  const [rows, setRows] = useState(null);
  const [roles, setRoles] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [permsFor, setPermsFor] = useState(null);
  const load = () => { setRows(null); api.get("/users").then((r) => setRows(r.data)); };
  useEffect(() => { api.get("/roles").then((r) => setRoles(r.data)).catch(() => {}); load(); }, []);
  return (
    <div>
      <PageHeader title="Pengguna" subtitle="Kelola akun & role pengguna sistem." testId="users-header">
        {can("users.manage") && <Button onClick={() => setOpen(true)} data-testid="create-user-btn" className="bg-indigo-600 hover:bg-indigo-500"><Plus className="mr-1.5 h-4 w-4" /> Tambah Pengguna</Button>}
      </PageHeader>
      <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
        {!rows ? <Loading /> : rows.length === 0 ? <EmptyState title="Belum ada pengguna" icon={UsersIcon} /> : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow><TableHead>Nama</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead><TableHead>Status</TableHead><TableHead>Dibuat</TableHead><TableHead></TableHead></TableRow></TableHeader>
              <TableBody>
                {rows.map((u) => (
                  <TableRow key={u.id} data-testid={`user-row-${u.email}`}>
                    <TableCell className="font-medium">{u.name}</TableCell><TableCell className="text-slate-500">{u.email}</TableCell>
                    <TableCell><span className="rounded-md bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700">{u.role_name}</span></TableCell>
                    <TableCell><StatusBadge status={u.status} label={u.status === "active" ? "Aktif" : "Nonaktif"} /></TableCell>
                    <TableCell className="text-xs text-slate-500">{formatDate(u.created_at)}</TableCell>
                    <TableCell className="flex gap-1">
                      {can("users.manage") && <Button variant="ghost" size="sm" onClick={() => setEdit(u)} data-testid={`edit-user-${u.email}`}><Pencil className="h-4 w-4" /></Button>}
                      {can("users.view") && <Button variant="ghost" size="sm" onClick={() => setPermsFor(u)} data-testid={`perms-user-${u.email}`} title="Permission & Override"><KeyRound className="h-4 w-4" /></Button>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
      {(open || edit) && <UserDialog roles={roles} row={edit} onClose={() => { setOpen(false); setEdit(null); }} onSaved={() => { setOpen(false); setEdit(null); load(); }} />}
      {permsFor && <UserPermsDialog row={permsFor} canManage={can("users.manage")} onClose={() => setPermsFor(null)} />}
    </div>
  );
}

function UserPermsDialog({ row, canManage, onClose }) {
  const { user } = useAuth();
  const isOwnerActor = user?.role === "OWNER_CEO";
  const [data, setData] = useState(null);
  const [perms, setPerms] = useState([]);
  const [state, setState] = useState({});
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([api.get(`/users/${row.id}/permissions`), api.get("/permissions")]).then(([d, p]) => {
      setData(d.data); setPerms(p.data);
      const st = {};
      (d.data.allow || []).forEach((c) => { st[c] = "allow"; });
      (d.data.deny || []).forEach((c) => { st[c] = "deny"; });
      setState(st);
    }).catch((e) => toast.error(apiError(e)));
  }, [row.id]);

  const setCell = (code, val, sensitive) => {
    if (sensitive && !isOwnerActor) return toast.error("Hanya OWNER_CEO yang dapat mengubah permission sensitif");
    setState((s) => ({ ...s, [code]: val }));
  };
  const save = async () => {
    setSaving(true);
    const allow = Object.entries(state).filter(([, v]) => v === "allow").map(([c]) => c);
    const deny = Object.entries(state).filter(([, v]) => v === "deny").map(([c]) => c);
    try { await api.put(`/users/${row.id}/permissions`, { allow, deny, reason }); toast.success("Override permission disimpan"); onClose(); }
    catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  const reset = async () => {
    try { await api.post(`/users/${row.id}/permissions/reset`); toast.success("Direset ke default role"); onClose(); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (!data) return null;
  const roleSet = new Set(data.role_permissions || []);
  const owner = roleSet.has("*");
  const byModule = perms.reduce((a, p) => { (a[p.module] = a[p.module] || []).push(p); return a; }, {});
  const effState = (p) => state[p.code] || "default";
  const isEff = (p) => owner || (roleSet.has(p.code) && effState(p) !== "deny") || effState(p) === "allow";

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="user-perms-dialog">
        <DialogHeader><DialogTitle className="font-display">Permission — {data.name}</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500">Role: <b>{data.role_name}</b>. Effective = Role + Allow − Deny (Deny menang). <ShieldCheck className="inline h-3 w-3 text-amber-500" /> hanya OWNER_CEO.</p>
        {owner ? <div className="rounded-lg border border-dashed p-6 text-center text-sm text-slate-500">Owner/CEO memiliki seluruh akses; override tidak berlaku.</div> : (
          <div className="space-y-4">
            {Object.entries(byModule).map(([mod, list]) => (
              <div key={mod}>
                <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">{mod}</p>
                <div className="space-y-1">
                  {list.map((p) => {
                    const st = effState(p);
                    const inRole = roleSet.has(p.code);
                    return (
                      <div key={p.code} data-testid={`uperm-${p.code}`} className="flex items-center gap-2 text-sm">
                        <span className="flex-1 flex items-center gap-1 text-slate-700">{p.sensitive && <ShieldCheck className="h-3 w-3 text-amber-500" />}{p.name}{inRole && <span className="text-[10px] text-slate-400">(default)</span>}</span>
                        {["default", "allow", "deny"].map((v) => (
                          <button key={v} disabled={!canManage || (p.sensitive && !isOwnerActor)} onClick={() => setCell(p.code, v, p.sensitive)}
                            data-testid={`uperm-${p.code}-${v}`}
                            className={"rounded px-2 py-0.5 text-xs border transition-colors " + (st === v ? (v === "allow" ? "bg-emerald-600 text-white border-emerald-600" : v === "deny" ? "bg-rose-600 text-white border-rose-600" : "bg-slate-600 text-white border-slate-600") : "border-slate-300 text-slate-500 hover:border-slate-400") + (!canManage || (p.sensitive && !isOwnerActor) ? " opacity-50 cursor-not-allowed" : "")}>
                            {v === "default" ? "Default" : v === "allow" ? "Allow" : "Deny"}
                          </button>
                        ))}
                        <span className={"w-12 text-right text-[10px] " + (isEff(p) ? "text-emerald-600" : "text-slate-400")}>{isEff(p) ? "aktif" : "-"}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
        {!owner && canManage && (
          <>
            <div><Label className="text-xs text-slate-500">Alasan (opsional)</Label><Input className="mt-1" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="uperm-reason" /></div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={reset} data-testid="uperm-reset"><RotateCcw className="mr-1 h-4 w-4" />Reset ke Default</Button>
              <Button onClick={save} disabled={saving} data-testid="uperm-save" className="bg-indigo-600 hover:bg-indigo-500">{saving ? "..." : "Simpan Override"}</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function UserDialog({ roles, row, onClose, onSaved }) {
  const [form, setForm] = useState({ name: row?.name || "", email: row?.email || "", password: "", role: row?.role || "", status: row?.status || "active" });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!form.name || !form.role || (!row && (!form.email || !form.password))) return toast.error("Lengkapi data pengguna");
    setSaving(true);
    try {
      if (row) {
        const body = { name: form.name, role: form.role, status: form.status };
        if (form.password) body.password = form.password;
        await api.put(`/users/${row.id}`, body);
      } else {
        await api.post("/users", form);
      }
      toast.success("Tersimpan"); onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-display">{row ? "Ubah" : "Tambah"} Pengguna</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div><Label className="text-xs text-slate-500">Nama</Label><Input className="mt-1" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="u-name" /></div>
          <div><Label className="text-xs text-slate-500">Email</Label><Input type="email" className="mt-1" value={form.email} disabled={!!row} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="u-email" /></div>
          <div><Label className="text-xs text-slate-500">{row ? "Kata Sandi Baru (opsional)" : "Kata Sandi"}</Label><Input type="password" className="mt-1" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="u-password" /></div>
          <div><Label className="text-xs text-slate-500">Role</Label><Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}><SelectTrigger className="mt-1" data-testid="u-role"><SelectValue placeholder="Pilih role..." /></SelectTrigger><SelectContent>{roles.map((r) => <SelectItem key={r.code} value={r.code}>{r.name}</SelectItem>)}</SelectContent></Select></div>
          {row && <div><Label className="text-xs text-slate-500">Status</Label><Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}><SelectTrigger className="mt-1" data-testid="u-status"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="active">Aktif</SelectItem><SelectItem value="inactive">Nonaktif</SelectItem></SelectContent></Select></div>}
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Batal</Button><Button onClick={save} disabled={saving} data-testid="save-user-btn" className="bg-indigo-600 hover:bg-indigo-500">{saving ? "..." : "Simpan"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
